/** Opens the document beside the app. */
const NEW_TAB_TARGET = '_blank';
/**
 * Cuts a fallback tab off from the app, as the pending tab is: no
 * `window.opener`, and no referrer sent to the storage host.
 */
const DETACHED_TAB_FEATURES = 'noopener,noreferrer';

/**
 * Opens a blank tab while the user activation is still valid. Safari and
 * mobile browsers refuse a `window.open` issued after an async request, so
 * the tab is opened first and navigated once the presigned URL is known. The
 * opener is severed so the document page cannot reach this window.
 *
 * @returns The tab handle, or `null` when the browser blocked the popup.
 */
export function openPendingDocumentTab(): Window | null {
  const tab = window.open('', NEW_TAB_TARGET);
  if (tab) {
    tab.opener = null;
  }
  return tab;
}

/**
 * Sends the pending tab to the signed URL, or opens a new, detached tab when
 * the pending one was blocked.
 *
 * @param params.tab - The pending tab, or `null`.
 * @param params.url - The presigned document URL.
 */
export function navigatePendingDocumentTab({
  tab,
  url,
}: {
  tab: Window | null;
  url: string;
}): void {
  if (tab) {
    tab.location.href = url;
    return;
  }
  window.open(url, NEW_TAB_TARGET, DETACHED_TAB_FEATURES);
}

/**
 * Closes the pending tab when the URL could not be fetched, so the user is
 * not left on a blank page.
 *
 * @param tab - The pending tab, or `null`.
 */
export function closePendingDocumentTab(tab: Window | null): void {
  tab?.close();
}
